import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';
import { ErrorMessage } from '@common/components/ErrorMessage';
import { getImmediateStartName } from '@common/components/Job/EnhanceJob/components/getImmediateStartName';
import {
  CustomVideoQuestion,
  ImmediateStart,
  RightToWork,
  TargetLocation,
  VideoQuestions
} from '@common/components/Job/formFields';
import { caja2Options } from '@common/components/Job/formFields/fields/RightToWork/options';
import { LOCALISED_COUNTRIES } from '@common/constants/data';
import { useLoadBusiness } from '@common/hooks/business';
import { useTranslation } from '@common/hooks/useTranslation';
import { AD_TYPE } from '@seek/je-shared-data';
import { SupportedCountryCode } from '@seek/je-shared-data/lib/types/brand';
import { upgraded } from '@src/job';
import { objectHasValues } from '@src/lib/func/objectHasValues';
import { NavControls } from '../../components';
import { CUSTOM_VIDEO_QUESTION_FIELD } from '../../formFields/fields/VideoQuestions/options';
import { FieldLabel } from '../components';
import { ImmediateStartIllustration } from '../components/ImmediateStartIllustration';

export type EnhanceFormData = {
  rightToWork: boolean;
  immediateStart: boolean;
  targetLocation: boolean;
  videoScreeningQuestion: string;
  customVideoQuestion?: string;
};

export type EnhanceFormRef = {
  clearErrors: UseFormReturn<EnhanceFormData>['clearErrors'];
  resetFormValues: UseFormReturn<EnhanceFormData>['reset'];
};

type Props = {
  disabled: boolean;
  onBack: () => void;
  onNext: (data?: EnhanceFormData) => void;
  onSubmit: (data?: EnhanceFormData) => void;
  defaultValues?: Partial<EnhanceFormData>;
  jobCountryCode: SupportedCountryCode;
  onNextText?: string;
  hideBackButton?: boolean;
  businessId: string;
  currentAdType?: AD_TYPE;
};

export const EnhanceJobForm = forwardRef<EnhanceFormRef, Props>(
  (props: Props, ref) => {
    const {
      onBack,
      onNext,
      onSubmit,
      defaultValues,
      disabled,
      jobCountryCode,
      onNextText,
      hideBackButton,
      businessId,
      currentAdType
    } = props;
    const { t } = useTranslation();
    const {
      handleSubmit,
      control,
      formState,
      clearErrors,
      reset,
      watch,
      setValue
    } = useForm<EnhanceFormData>({
      defaultValues,
      values: objectHasValues(defaultValues) ? defaultValues : undefined,
      disabled
    });
    const { data: business } = useLoadBusiness(businessId);

    useImperativeHandle(ref, () => ({
      clearErrors,
      resetFormValues: reset
    }));

    const rightToWorkValue = watch('rightToWork');
    const videoScreeningQuestionValue = watch('videoScreeningQuestion');
    const isTargetLocationFieldDisabled = !rightToWorkValue;
    const isRenderCustomVideoQuestionField =
      videoScreeningQuestionValue === CUSTOM_VIDEO_QUESTION_FIELD;

    useEffect(() => {
      if (isTargetLocationFieldDisabled) {
        setValue('targetLocation', false);
      }
    }, [isTargetLocationFieldDisabled, setValue]);

    const onFormSubmit = async (data: EnhanceFormData) => {
      await onSubmit(data);
      onNext(data);
    };

    const onFormError = () => {
      if (disabled) {
        clearErrors();
        onNext();
      }
    };

    const isFormInvalid =
      formState.isSubmitted && !formState.isValid && !disabled;

    const immediateStartText = t(getImmediateStartName(jobCountryCode));

    const alreadyHasVideoQuestions =
      upgraded(currentAdType) && Boolean(defaultValues?.videoScreeningQuestion);

    return (
      <form onSubmit={handleSubmit(onFormSubmit, onFormError)}>
        <div
          className="flex flex-col gap-lg"
          data-testid="enhance-job-form-jse-sg"
        >
          <RightToWork
            name="rightToWork"
            control={control}
            options={caja2Options(t)}
            label={
              <FieldLabel
                text={t('postJobForm.fieldLabel.rightToWorkQuestion', {
                  country: LOCALISED_COUNTRIES(t)[jobCountryCode]
                })}
              />
            }
          />
          <TargetLocation
            name="targetLocation"
            control={control}
            label={
              <FieldLabel
                text={t('postJobForm.fieldLabel.targetLocation')}
                description={t(
                  'postJobForm.fieldLabel.targetLocationDescription'
                )}
              />
            }
            disabled={isTargetLocationFieldDisabled}
          />
          <ImmediateStart
            name="immediateStart"
            control={control}
            options={{
              illustration: (
                <ImmediateStartIllustration jobCountryCode={jobCountryCode} />
              )
            }}
            label={
              <FieldLabel
                text={t('postJobForm.fieldLabel.immediateStart', {
                  immediateStartName: immediateStartText
                })}
                description={t(
                  'postJobForm.fieldLabel.immediateStartDescription'
                )}
              />
            }
          />
          <VideoQuestions
            name="videoScreeningQuestion"
            control={control}
            label={
              <FieldLabel
                text={t('postJobForm.fieldLabel.videoScreeningQuestions')}
                description={t(
                  'postJobForm.fieldLabel.videoQuestionsDescriptions'
                )}
              />
            }
            businessName={business?.name}
            disabled={alreadyHasVideoQuestions}
          />
          {isRenderCustomVideoQuestionField && (
            <CustomVideoQuestion
              name="customVideoQuestion"
              control={control}
              disabled={alreadyHasVideoQuestions}
            />
          )}

          <div className="flex flex-col gap-sm mt-sm">
            <NavControls
              onBack={onBack}
              onNextText={onNextText}
              hideBackButton={hideBackButton}
              onNext={handleSubmit(onFormSubmit, onFormError)}
              disabled={formState.isSubmitting}
            />

            {isFormInvalid && (
              <ErrorMessage>{t('validations.general.summary')}</ErrorMessage>
            )}
          </div>
        </div>
      </form>
    );
  }
);
